import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Toolbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  AddCircle,
  Clear,
  Undo,
  Save,
  Edit,
  CheckCircle,
  Error,
  Close,
  Warning,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { getAnnotationImageFromS3 } from '../services/motorApiService';

const ImageAnnotationPanel = ({ vehicleImagePath, onImageAnnotated, contractNo, vehicleId, onAnnotationStatusChange, disabled = false, isSubmitted = false, wasOriginallySubmitted = false, isReInspection = false, isUnlockedForEditing = false }) => {
  const canvasRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [error, setError] = useState('');
  const [annotations, setAnnotations] = useState([]);
  const [isAddingText, setIsAddingText] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });
  const [currentText, setCurrentText] = useState('');
  const [vehicleImage, setVehicleImage] = useState(null);
  const [originalImageDimensions, setOriginalImageDimensions] = useState({ width: 386, height: 570 });
  const [saveAlertOpen, setSaveAlertOpen] = useState(false);
  const [saveAlertType, setSaveAlertType] = useState('success'); // 'success' or 'error'
  const [saveAlertMessage, setSaveAlertMessage] = useState('');
  const [isLoadingOriginalImage, setIsLoadingOriginalImage] = useState(false);
  const [currentImageType, setCurrentImageType] = useState('original'); // 'original' or 'annotated'
  const [currentBlobUrl, setCurrentBlobUrl] = useState(null);
  const [draggingAnnotation, setDraggingAnnotation] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Get canvas coordinates from mouse event
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  // Find annotation at given canvas coordinates
  const findAnnotationAt = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    const canvasElement = canvasRef.current;
    const displayedWidth = canvasElement.getBoundingClientRect().width;
    const scaleFactor = canvas.width / displayedWidth;
    const fontSize = Math.round(28 * scaleFactor);
    ctx.font = `bold ${fontSize}px Arial`;

    // Check annotations in reverse order (top-most first)
    for (let i = annotations.length - 1; i >= 0; i--) {
      const ann = annotations[i];
      const metrics = ctx.measureText(ann.text);
      const textWidth = metrics.width;
      const textHeight = fontSize;
      // Hit area: from (ann.x, ann.y - textHeight) to (ann.x + textWidth, ann.y)
      if (x >= ann.x - 5 && x <= ann.x + textWidth + 5 &&
          y >= ann.y - textHeight - 5 && y <= ann.y + 5) {
        return { annotation: ann, index: i };
      }
    }
    return null;
  };

  // Handle canvas clicks - memoized to avoid recreating
  const handleCanvasClick = useCallback((e) => {
    if (isAddingText && !disabled) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      
      // Calculate the scale factor between displayed size and actual canvas size
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      
      // Convert click coordinates to canvas coordinates (accounting for magnification)
      const x = clickX * scaleX;
      const y = clickY * scaleY;
      
      // Ensure coordinates are within canvas bounds
      const boundedX = Math.max(0, Math.min(canvas.width, x));
      const boundedY = Math.max(0, Math.min(canvas.height, y));
      
      setDialogPosition({ x: boundedX, y: boundedY });
      setDialogOpen(true);
    }
  }, [isAddingText, disabled]);

  // Mouse down - start dragging if on an annotation
  const handleMouseDown = useCallback((e) => {
    if (disabled || isAddingText) return;
    const coords = getCanvasCoords(e);
    const hit = findAnnotationAt(coords.x, coords.y);
    if (hit) {
      setDraggingAnnotation(hit.index);
      setDragOffset({ x: coords.x - hit.annotation.x, y: coords.y - hit.annotation.y });
      e.preventDefault();
    }
  }, [disabled, isAddingText, annotations]);

  // Mouse move - update annotation position while dragging
  const handleMouseMove = useCallback((e) => {
    if (draggingAnnotation === null) return;
    const coords = getCanvasCoords(e);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const newX = Math.max(0, Math.min(canvas.width - 20, coords.x - dragOffset.x));
    const newY = Math.max(20, Math.min(canvas.height, coords.y - dragOffset.y));

    setAnnotations(prev => prev.map((ann, idx) =>
      idx === draggingAnnotation ? { ...ann, x: newX, y: newY } : ann
    ));
  }, [draggingAnnotation, dragOffset]);

  // Mouse up - stop dragging
  const handleMouseUp = useCallback(() => {
    if (draggingAnnotation !== null) {
      setDraggingAnnotation(null);
      if (onAnnotationStatusChange) onAnnotationStatusChange(true);
    }
  }, [draggingAnnotation, onAnnotationStatusChange]);

  useEffect(() => {
    // console.log('🔄 Image loading useEffect triggered by:', { isSubmitted, wasOriginallySubmitted, contractNo, disabled });
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // We'll set canvas dimensions after the image loads to get actual dimensions
    const magnificationFactor = 2.0; // Adjust this value to control magnification

    // Load annotated image for submitted inspections
    const loadAnnotatedImage = async (img) => {
      try {
        console.log('🖼️ Loading annotated image from S3 for vehicleId:', vehicleId);
        
        // Fetch annotated image from S3
        const imageBase64 = await getAnnotationImageFromS3(vehicleId);
        
        if (imageBase64) {
          // Clean up previous blob URL if it exists
          if (currentBlobUrl) {
            URL.revokeObjectURL(currentBlobUrl);
          }
          console.log('✅ Successfully loaded annotated image from S3');
          img.src = imageBase64;
          setCurrentImageType('annotated');
          // Clear in-memory annotations since they're already baked into the annotated image
          setAnnotations([]);
        } else {
          console.log('❌ Annotated image not found in S3, falling back to Vehicle.jpg');
          // Fallback to original vehicle image if annotated image not found
          img.src = '/Vehicle.jpg';
          setCurrentImageType('original');
        }
      } catch (error) {
        console.error('❌ Failed to load annotated image from S3:', error);
        // Fallback to original vehicle image
        img.src = '/Vehicle.jpg';
        setCurrentImageType('original');
      }
    };

    // Load original Vehicle.jpg image
    const loadOriginalImage = (img) => {
      console.log('📥 Loading original Vehicle.jpg image');
      img.src = '/Vehicle.jpg';
      setCurrentImageType('original');
      // Clear annotations when loading original image
      setAnnotations([]);
      if (onAnnotationStatusChange) {
        onAnnotationStatusChange(false);
      }
    };

    // Load vehicle image
    const loadImage = () => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Add debug logging when image loads
        console.log('🔍 LOAD DEBUG - Image loaded:', {
          imgNaturalWidth: img.naturalWidth,
          imgNaturalHeight: img.naturalHeight,
          imgWidth: img.width,
          imgHeight: img.height,
          currentImageType,
          magnificationFactor,
          previousOriginalImageDimensions: originalImageDimensions
        });
        
        // Store original image dimensions but always use standard size for annotation saving
        // Keep the loaded image dimensions for display but use standard 386x570 for saving
        console.log('🔍 LOAD DEBUG - Loaded image actual dimensions:', { width: img.width, height: img.height });
        // Always keep originalImageDimensions as 386x570 for consistent annotation saving
        // setOriginalImageDimensions({ width: img.width, height: img.height }); // Commented out - this was causing the issue
        // Set canvas dimensions based on actual image dimensions
        canvas.width = img.width * magnificationFactor;
        canvas.height = img.height * magnificationFactor;
        
        console.log('🔍 LOAD DEBUG - Canvas dimensions set:', {
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          calculatedFrom: `${img.width} x ${img.height} * ${magnificationFactor}`
        });
        
        setVehicleImage(img);
        setImageLoaded(true);
        setError('');
      };
      
      img.onerror = () => {
        console.warn('Image failed to load, drawing placeholder');
        // Set default dimensions for placeholder (using original dimensions you specified)
        canvas.width = 193 * magnificationFactor;
        canvas.height = 285 * magnificationFactor;
        
        setVehicleImage(null);
        setImageLoaded(true);
        setError(disabled ? 'Annotated image not found' : 'Using placeholder template (Vehicle.jpg not found)');
      };
      
      // Determine which image to load based on original submission status
      if (wasOriginallySubmitted && vehicleId && !isReInspection) {
        loadAnnotatedImage(img);
      } else if (isUnlockedForEditing && vehicleId) {
        // For unlock-for-editing mode, load the annotated image so user can add more annotations on top
        loadAnnotatedImage(img);
      } else {
        loadOriginalImage(img);
      }
    };




    loadImage();

    // Cleanup
    return () => {
      // Canvas click listener cleanup is handled in separate useEffect
    };
  }, [wasOriginallySubmitted, vehicleId, disabled, isReInspection, isUnlockedForEditing]); // Only depend on props that should trigger image reload

  // Separate useEffect for canvas click and drag handling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('click', handleCanvasClick);
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseUp);

    return () => {
      canvas.removeEventListener('click', handleCanvasClick);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [handleCanvasClick, handleMouseDown, handleMouseMove, handleMouseUp]);

  // Separate useEffect to redraw canvas when annotations change
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const drawCanvas = () => {
      console.log('🎨 Drawing canvas - annotations count:', annotations.length, 'vehicleImage exists:', !!vehicleImage);
      console.log('⏱️ Canvas draw timestamp:', Date.now());
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      if (vehicleImage) {
        // Draw the actual vehicle image to fill the entire canvas
        ctx.drawImage(vehicleImage, 0, 0, canvas.width, canvas.height);
        console.log('🖼️ Drew vehicle image');
      } else {
        // Draw placeholder vehicle
        drawPlaceholder(ctx);
        console.log('🎯 Drew placeholder');
      }
      
      // Draw all annotations
      annotations.forEach((annotation, index) => {
        console.log(`📍 Drawing annotation ${index + 1}:`, annotation.text, 'at', annotation.x, annotation.y);
        drawAnnotation(ctx, annotation);
      });
      console.log('✅ Canvas drawing complete');
    };

    drawCanvas();
  }, [annotations, vehicleImage]); // This useEffect runs when annotations change

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [currentBlobUrl]);

  // Note: Canvas drawing is handled by the main useEffect above

  const drawPlaceholder = (ctx) => {
    // Clear canvas
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Scale placeholder elements to match canvas size
    const scaleX = ctx.canvas.width / 500; // Original placeholder was designed for 500px width
    const scaleY = ctx.canvas.height / 375; // Original placeholder was designed for 375px height
    
    // Draw simple vehicle outline scaled to canvas
    ctx.strokeStyle = '#6c757d';
    ctx.lineWidth = 2;
    
    // Car body
    ctx.fillStyle = '#e9ecef';
    ctx.fillRect(100 * scaleX, 150 * scaleY, 300 * scaleX, 100 * scaleY);
    ctx.strokeRect(100 * scaleX, 150 * scaleY, 300 * scaleX, 100 * scaleY);
    
    // Car roof
    ctx.fillRect(150 * scaleX, 110 * scaleY, 200 * scaleX, 40 * scaleY);
    ctx.strokeRect(150 * scaleX, 110 * scaleY, 200 * scaleX, 40 * scaleY);
    
    // Wheels
    ctx.fillStyle = '#495057';
    ctx.beginPath();
    ctx.arc(150 * scaleX, 270 * scaleY, 20 * Math.min(scaleX, scaleY), 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(350 * scaleX, 270 * scaleY, 20 * Math.min(scaleX, scaleY), 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
    
    // Windows
    ctx.fillStyle = '#ced4da';
    ctx.fillRect(160 * scaleX, 120 * scaleY, 180 * scaleX, 30 * scaleY);
    ctx.strokeRect(160 * scaleX, 120 * scaleY, 180 * scaleX, 30 * scaleY);
    
    // Add text
    ctx.fillStyle = '#6c757d';
    ctx.font = `${14 * Math.min(scaleX, scaleY)}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText('Vehicle Template - Click to add damage codes', ctx.canvas.width / 2, 320 * scaleY);
  };

  const drawAnnotation = (ctx, annotation) => {
    ctx.save();
    
    // FINAL FIX: Account for CSS scaling of canvas
    // The canvas is drawn at full size but CSS-scaled down for display
    // We need fonts to look good at the displayed size, not the canvas size
    
    const canvas = ctx.canvas;
    
    // Get the actual displayed size of the canvas element
    const canvasElement = canvasRef.current;
    const displayedWidth = canvasElement ? canvasElement.getBoundingClientRect().width : canvas.width;
    const displayedHeight = canvasElement ? canvasElement.getBoundingClientRect().height : canvas.height;
    
    // Calculate the scale factor between actual canvas size and displayed size
    const scaleFactorX = canvas.width / displayedWidth;
    const scaleFactorY = canvas.height / displayedHeight;
    const scaleFactor = Math.max(scaleFactorX, scaleFactorY); // Use the larger scale factor
    
    // We want the font to appear as 28px at the displayed size
    const targetDisplayFontSize = 28;
    const fontSize = Math.round(targetDisplayFontSize * scaleFactor);
    
    // Enhanced debug logging for font calculation
    console.log('🔤 FONT DEBUG - Drawing annotation (CSS SCALING FIX):', {
      annotationText: annotation.text,
      canvasActualSize: `${canvas.width}x${canvas.height}`,
      canvasDisplayedSize: `${displayedWidth.toFixed(0)}x${displayedHeight.toFixed(0)}`,
      scaleFactorX: scaleFactorX.toFixed(2),
      scaleFactorY: scaleFactorY.toFixed(2),
      usedScaleFactor: scaleFactor.toFixed(2),
      targetDisplayFontSize,
      calculatedFontSize: fontSize,
      currentImageType,
      originalImageDimensions,
      fontScalingMethod: 'CSS scaling compensation'
    });
    
    // Measure text to create background
    ctx.font = `bold ${fontSize}px Arial`;
    const textMetrics = ctx.measureText(annotation.text);
    const textWidth = textMetrics.width;
    const textHeight = fontSize;
    const padding = 4;
    
    // Draw text only in bright red color (no background)
    ctx.fillStyle = '#ff0000';
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(annotation.text, annotation.x, annotation.y);
    
    ctx.restore();
  };

  const enableTextMode = () => {
    if (!disabled) {
      setIsAddingText(true);
    }
  };

  const disableTextMode = () => {
    setIsAddingText(false);
  };

  const addTextAnnotation = () => {
    if (!currentText.trim()) return;

    const newAnnotation = {
      id: Date.now(),
      text: currentText.trim(),
      x: dialogPosition.x,
      y: dialogPosition.y,
    };
    
    console.log('➕ Adding annotation:', newAnnotation);
    
    setAnnotations(prev => {
      const newAnnotations = [...prev, newAnnotation];
      console.log('📝 Updated annotations array:', newAnnotations);
      console.log('⏰ Timestamp:', Date.now());
      // Notify parent that there are unsaved annotations
      if (onAnnotationStatusChange) {
        onAnnotationStatusChange(true);
      }
      return newAnnotations;
    });
    setDialogOpen(false);
    setCurrentText('');
    disableTextMode();
  };

  const clearAnnotations = () => {
    if (!disabled) {
      if (wasOriginallySubmitted && currentImageType === 'annotated') {
        // For submitted inspections with annotated image, load original Vehicle.jpg
        console.log('🔄 Clearing annotations - loading original Vehicle.jpg for submitted inspection');
        setIsLoadingOriginalImage(true);
        
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          setVehicleImage(img);
          setCurrentImageType('original');
          setAnnotations([]);
          setIsLoadingOriginalImage(false);
          if (onAnnotationStatusChange) {
            onAnnotationStatusChange(false);
          }
        };
        
        img.onerror = () => {
          console.error('Failed to load original image');
          setIsLoadingOriginalImage(false);
          setError('Failed to load original vehicle image');
        };
        
        // Load original Vehicle.jpg image directly
        console.log('📥 Loading original Vehicle.jpg image');
        img.src = '/Vehicle.jpg';
        setCurrentImageType('original');
      } else {
        // For new inspections or already original image, just clear annotations
        setAnnotations([]);
        if (onAnnotationStatusChange) {
          onAnnotationStatusChange(false);
        }
      }
    }
  };

  const undoLastAnnotation = () => {
    if (!disabled) {
      setAnnotations(prev => {
        const newAnnotations = prev.slice(0, -1);
        // Notify parent about annotation status
        if (onAnnotationStatusChange) {
          onAnnotationStatusChange(newAnnotations.length > 0);
        }
        return newAnnotations;
      });
    }
  };

  const saveAnnotation = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !vehicleId) {
      setError('Vehicle ID is required to save annotation');
      return;
    }
    
    try {
      // Add debug logging before save
      console.log('🔍 SAVE DEBUG - Before save:', {
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        originalImageDimensions,
        currentImageType,
        vehicleImageExists: !!vehicleImage,
        vehicleImageDimensions: vehicleImage ? { width: vehicleImage.width, height: vehicleImage.height } : null,
        annotationsCount: annotations.length
      });
      
      // CRITICAL FIX: Create a new canvas at the ORIGINAL dimensions (386x570)
      const saveCanvas = document.createElement('canvas');
      const saveCtx = saveCanvas.getContext('2d');
      
      // FORCE save canvas to ALWAYS be standard dimensions (386x570)
      // Regardless of what the loaded image dimensions are, we always save at standard size
      const STANDARD_WIDTH = 386;
      const STANDARD_HEIGHT = 570;
      saveCanvas.width = STANDARD_WIDTH;
      saveCanvas.height = STANDARD_HEIGHT;
      
      console.log('📐 SAVE DEBUG - Save canvas dimensions:', {
        saveWidth: saveCanvas.width,
        saveHeight: saveCanvas.height,
        forcedToStandard: `${STANDARD_WIDTH}x${STANDARD_HEIGHT}`,
        originalImageDimensions: originalImageDimensions,
        note: 'Always saving at standard size regardless of loaded image dimensions'
      });
      
      // Draw the vehicle image at original size
      if (vehicleImage) {
        saveCtx.drawImage(vehicleImage, 0, 0, saveCanvas.width, saveCanvas.height);
        console.log('🖼️ Drew vehicle image to save canvas at original size');
      } else {
        // Draw placeholder if no vehicle image
        saveCtx.fillStyle = '#f8f9fa';
        saveCtx.fillRect(0, 0, saveCanvas.width, saveCanvas.height);
        console.log('🎯 Drew placeholder to save canvas');
      }
      
      // Draw annotations scaled to original dimensions
      annotations.forEach((annotation, index) => {
        // Calculate scale factor from magnified canvas to original size
        const scaleX = saveCanvas.width / canvas.width;
        const scaleY = saveCanvas.height / canvas.height;
        
        const scaledAnnotation = {
          ...annotation,
          x: annotation.x * scaleX,
          y: annotation.y * scaleY
        };
        
        console.log(`📍 Drawing scaled annotation ${index + 1}: original(${annotation.x}, ${annotation.y}) -> scaled(${scaledAnnotation.x}, ${scaledAnnotation.y})`);
        
        // MATCH DISPLAY STYLE: Use the same annotation drawing logic as the display function
        // Calculate appropriate font size for 386x570 save canvas
        // The display uses targetDisplayFontSize = 28, we need to scale this for our save canvas
        
        // For 386x570 save canvas, we want text to be proportional to what's shown in display
        // Since display shows ~28px at user's screen, we calculate equivalent for save size
        const targetSaveDisplayFontSize = 28; // Same base size as display
        const saveFontSize = Math.round(targetSaveDisplayFontSize); // Direct size for 386x570
        
        console.log(`🔤 SAVE FONT DEBUG - Annotation ${index + 1}:`, {
          text: scaledAnnotation.text,
          position: `${scaledAnnotation.x}, ${scaledAnnotation.y}`,
          fontSize: saveFontSize,
          matchingDisplayStyle: 'true'
        });
        
        // Draw text ONLY (no circle/dot) - matching display function exactly
        saveCtx.fillStyle = '#ff0000'; // Same bright red as display
        saveCtx.font = `bold ${saveFontSize}px Arial`; // Same bold font as display
        saveCtx.textAlign = 'left'; // Same alignment as display
        saveCtx.fillText(scaledAnnotation.text, scaledAnnotation.x, scaledAnnotation.y); // Same positioning as display
      });
      
      // Save at the ORIGINAL dimensions (386x570)
      saveCanvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], 'annotated-vehicle.png', { type: 'image/png' });
          
          // Debug: Check the actual file size and estimated dimensions
          console.log('💾 SAVE DEBUG - File created at ORIGINAL size:', {
            blobSize: blob.size,
            fileName: file.name,
            fileType: file.type,
            expectedDimensions: `${saveCanvas.width}x${saveCanvas.height}`,
            correctSize: saveCanvas.width === 386 && saveCanvas.height === 570
          });
          
          try {
            // Upload to backend immediately
            const response = await apiService.uploadAnnotation(vehicleId, file);
            
            // Also pass to parent component for inspection submission
            onImageAnnotated(file);
            
            // Show success popup
            setSaveAlertType('success');
            // Sanitize contract number for filename display (UCC/P00001/2025 -> P00001_2025)
            setSaveAlertMessage(`Annotation saved as ${vehicleId}.png (${saveCanvas.width}x${saveCanvas.height} pixels)`);
            setSaveAlertOpen(true);
            setError('');
            
            // Notify parent that annotations are now saved
            if (onAnnotationStatusChange) {
              onAnnotationStatusChange(false);
            }
          } catch (uploadError) {
            console.error('Error uploading annotation:', uploadError);
            
            // Show error popup
            setSaveAlertType('error');
            setSaveAlertMessage('Failed to save annotation to backend. Please try again.');
            setSaveAlertOpen(true);
            
            // Still pass to parent for inspection submission as fallback
            onImageAnnotated(file);
          }
        }
      }, 'image/png');
    } catch (err) {
      console.error('Error saving annotation:', err);
      
      // Show error popup
      setSaveAlertType('error');
      setSaveAlertMessage('Failed to save annotation. Please check your internet connection and try again.');
      setSaveAlertOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentText('');
    disableTextMode();
  };

  return (
    <Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {disabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Annotation panel is disabled. The inspection has been submitted. Click the unlock button to make changes.
        </Alert>
      )}

      {isLoadingOriginalImage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading original vehicle image...
        </Alert>
      )}

      {/* Canvas */}
      <Box sx={{ 
        border: 1, 
        borderColor: 'grey.300', 
        mb: 2, 
        position: 'relative', 
        width: '100%',
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: '#f8f9fa',
        aspectRatio: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& canvas': {
          // Always use magnified size (2x) regardless of screen width
          maxWidth: `${originalImageDimensions.width * 2}px !important`,
        }
      }}>
        <canvas
          ref={canvasRef}
          style={{
            border: 'none',
            cursor: (isAddingText && !disabled) ? 'crosshair' : disabled ? 'not-allowed' : (draggingAnnotation !== null ? 'grabbing' : 'default'),
            display: 'block',
            width: '100%',
            height: 'auto',
            borderRadius: '4px',
            opacity: disabled ? 0.6 : 1,
          }}
        />
        {isAddingText && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(220, 53, 69, 0.1)',
              color: '#dc3545',
              padding: 1,
              textAlign: 'center',
              fontSize: '0.875rem',
              fontWeight: 'bold',
            }}
          >
            Click on the image to add damage code
          </Box>
        )}
      </Box>

      {/* Controls */}
      <Toolbar sx={{ p: 0, minHeight: 'auto !important', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant={isAddingText ? 'contained' : 'outlined'}
          startIcon={<AddCircle />}
          onClick={enableTextMode}
          size="small"
          color="primary"
          disabled={disabled}
        >
          Add Damage Code
        </Button>
        
        <IconButton onClick={undoLastAnnotation} size="small" disabled={disabled || annotations.length === 0}>
          <Undo />
        </IconButton>
        
        <IconButton 
          onClick={clearAnnotations} 
          size="small" 
          disabled={disabled || (annotations.length === 0 && (!wasOriginallySubmitted || currentImageType === 'original'))}
          title={wasOriginallySubmitted && currentImageType === 'annotated' ? 'Load original image' : 'Clear annotations'}
        >
          <Clear />
        </IconButton>
        
        <IconButton onClick={saveAnnotation} size="small" color="primary" disabled={disabled}>
          <Save />
        </IconButton>
      </Toolbar>

      {/* Annotations Summary */}
      {annotations.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Damage Codes Added: {annotations.map(ann => ann.text).join(', ')}
          </Typography>
        </Box>
      )}

      {/* Instructions */}
      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">
          <strong>Instructions:</strong>
        </Typography>
        <Typography variant="body2" color="textSecondary">
          1. Click "Add Damage Code" button
        </Typography>
        <Typography variant="body2" color="textSecondary">
          2. Click on the vehicle image where damage is located
        </Typography>
        <Typography variant="body2" color="textSecondary">
          3. Enter damage code (e.g., A1, B2, C3)
        </Typography>
        <Typography variant="body2" color="textSecondary">
          4. Use Undo/Clear to manage annotations
        </Typography>
        <Typography variant="body2" color="textSecondary">
          5. Click Save when finished
        </Typography>
      </Box>

      {/* Text Input Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Edit />
            Add Damage Code
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Damage Code (e.g., A1, B2, C3)"
            fullWidth
            variant="outlined"
            value={currentText}
            onChange={(e) => setCurrentText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addTextAnnotation();
              }
            }}
            helperText="Enter a code to identify this damage area"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={addTextAnnotation}
            variant="contained"
            disabled={!currentText.trim()}
          >
            Add Code
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modern Save Alert Popup */}
      <Dialog 
        open={saveAlertOpen} 
        onClose={() => setSaveAlertOpen(false)}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 2,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }
        }}
      >
        <DialogContent sx={{ padding: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            {/* Icon */}
            {saveAlertType === 'success' ? (
              <CheckCircle 
                sx={{ 
                  fontSize: 64, 
                  color: '#4caf50',
                  filter: 'drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3))'
                }} 
              />
            ) : (
              <Error 
                sx={{ 
                  fontSize: 64, 
                  color: '#f44336',
                  filter: 'drop-shadow(0 4px 8px rgba(244, 67, 54, 0.3))'
                }} 
              />
            )}
            
            {/* Title */}
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                color: saveAlertType === 'success' ? '#2e7d32' : '#c62828',
                marginBottom: 1,
              }}
            >
              {saveAlertType === 'success' ? 'Save Successful!' : 'Save Failed'}
            </Typography>
            
            {/* Message */}
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666',
                fontSize: '1rem',
                lineHeight: 1.6,
                textAlign: 'center',
                maxWidth: 400,
              }}
            >
              {saveAlertMessage}
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'center', paddingBottom: 3 }}>
          <Button
            onClick={() => setSaveAlertOpen(false)}
            variant="contained"
            size="large"
            startIcon={<Close />}
            sx={{
              borderRadius: 2,
              padding: '12px 32px',
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              background: saveAlertType === 'success' 
                ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                : 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                background: saveAlertType === 'success'
                  ? 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)'
                  : 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageAnnotationPanel;